import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { useIntl } from '@leagueplatform/locales';
import { captureAppError } from '@leagueplatform/observability';
import {
  ErrorState,
  LoadingIndicator,
} from '@leagueplatform/web-common-components';
import { SENTRY_CONSTANTS } from 'common/constants';
import { endAgentSession, startAgentSession } from 'utils/agent-session';
import { signOut } from 'utils/sign-out';

export const AGENT_SAML_CONNECTION = 'geisinger-impersonation-saml';

export const AuthViewLogout = () => {
  useEffect(() => {
    signOut();
  }, []);

  return <LoadingIndicator />;
};

// Auth0 reports a failed support login back on this route, in the query or,
// for the implicit (`id_token`) response, in the fragment.
const getAuthErrorType = (search: string, hash: string) =>
  new URLSearchParams(search).get('error') ||
  new URLSearchParams(hash.replace(/^#/, '')).get('error');

// The query is honoured only for support SAML connections. This route skips the
// member `prompt: 'login'`, so pointing it at a member connection would sign a
// member in from a leftover IdP session without asking for credentials.
const getAgentConnection = (search: string) => {
  const requested = new URLSearchParams(search).get('connection');
  return requested?.endsWith('-impersonation-saml')
    ? requested
    : AGENT_SAML_CONNECTION;
};

// Auth0 sends the agent here once the support SAML login has succeeded, with a
// token the app does not consume. Starting the app's own login on the same
// connection completes straight away, because the SAML provider already holds
// the agent's session, and returns through the normal code callback.
export const AuthViewStartAgentSession = () => {
  const { search, hash } = useLocation();
  const { formatMessage } = useIntl();
  const authErrorType = getAuthErrorType(search, hash);

  useEffect(() => {
    if (authErrorType) {
      // The flag may survive from the attempt that just failed. Left in place,
      // a member signing in later in this tab, whose session then lapses, would
      // be signed out without `federated`.
      endAgentSession();
      // Only the error code: the description can name the account involved.
      captureAppError(
        new Error(`Support agent sign-in failed: ${authErrorType}`),
        {
          ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
          errorName: 'Support Agent Sign-In Failed',
        },
      );
      // Restarting the login here would loop straight back to this error.
      return;
    }

    startAgentSession();
    StandaloneAuth.client.loginWithRedirect({
      connection: getAgentConnection(search),
      // The member-wide `prompt: 'login'` would make the SAML provider ask the
      // agent to sign in a second time. An undefined value is dropped from the
      // authorize URL.
      prompt: undefined,
      // Replace this entry so Back can't land here and silently start a new
      // agent session after sign-out.
      redirectMethod: 'replace',
    });
  }, [authErrorType, search]);

  if (authErrorType) {
    return (
      <ErrorState
        headingString={formatMessage({ id: 'STR_ERROR_STATE_HEADING_GENERIC' })}
        bodyString={formatMessage({ id: 'STR_AGENT_SIGN_IN_ERROR' })}
        hideButton
        error={null}
      />
    );
  }

  return <LoadingIndicator />;
};
